import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { OverviewDeferredSaveApi } from '@/lib/capability-detail-context';
import { useCapabilityDetail as useCapabilityDetailData } from '@/hooks/use-entity-detail';
import { useCapabilityUpdate } from '@/hooks/use-entity-mutations';
import { usePageTitle } from '@/hooks/use-page-title';
import { useHeaderAction } from '@/lib/header-action-context';
import { useCapabilityDetail as useCapabilityDetailShell } from '@/lib/capability-detail-context';
import { toast } from 'sonner';
import { useParams } from 'react-router';
import {
   Select,
   SelectContent,
   SelectItem,
   SelectTrigger,
   SelectValue,
} from '@/components/ui/select';
import type {
   OverviewPersistMode,
   DeferredOverviewDirtyPayload,
} from './CapabilityOverviewTab';
import { CapabilitySettingsSheet } from './CapabilitySettingsSheet';
import { getCapabilityColors, getStatusStyle } from '@/lib/capability-colors';
import { capabilityDisplayTitle, CAPABILITY_TYPE_LABELS } from '@/types';
import type { CapabilityRevision } from '@/types';
import { WorkerHostingInfo } from './WorkerHostingInfo';
import { GitBranch, Lock } from 'lucide-react';

export const WORKING_COPY_ID = '__working__';

interface CapabilityDetailLayoutProps {
   typeLabel: string;
   children?: ReactNode;
   overviewPersistMode?: OverviewPersistMode;
   onOverviewDeferredDirtyChange?: (payload: DeferredOverviewDirtyPayload) => void;
   /** Parent-owned overview draft (visibility/status/tags/…) for deferred settings. */
   pendingOverviewPatch?: Record<string, unknown>;
   /** Save deferred overview (+ any page-level dirty config) from the settings sheet. */
   onSaveDeferred?: () => void;
   isOverviewDirty?: boolean;
   isSavingOverview?: boolean;
   overviewFormDisabled?: boolean;
   supplementalDeferredSave?: Pick<OverviewDeferredSaveApi, 'isDirty' | 'isSaving' | 'save'> | null;
   selectedRevisionId?: string;
   onRevisionChange?: (revisionId: string) => void;
   /** Extra content rendered inside the Settings sheet's Overview tab (e.g. provider info chips). */
   overviewChildren?: ReactNode;
   /** When provided, skip internal data fetch (Plan 30 tiled-window support). */
   entityData?: Record<string, unknown>;
   /** Override useParams().appKey (for embedded/tiled context). */
   appKey?: string;
   /** Override useParams().name (for embedded/tiled context). */
   capabilityName?: string;
   /** When provided, use pushHeaderLayer for center + versionSelector instead of the base-layer setters. */
   headerLayerId?: string;
   /** Edge-to-edge content (no outer padding) — for full-canvas graph editors. */
   fullBleed?: boolean;
}

export function CapabilityDetailLayout({
   typeLabel,
   children,
   overviewPersistMode = 'deferred',
   onOverviewDeferredDirtyChange,
   pendingOverviewPatch,
   onSaveDeferred,
   isOverviewDirty = false,
   isSavingOverview = false,
   overviewFormDisabled = false,
   supplementalDeferredSave = null,
   selectedRevisionId,
   onRevisionChange,
   overviewChildren,
   entityData: entityDataProp,
   appKey: appKeyProp,
   capabilityName: capabilityNameProp,
   headerLayerId,
   fullBleed = false,
}: CapabilityDetailLayoutProps) {
   const routeParams = useParams();
   const appKey = appKeyProp ?? routeParams.appKey;
   const name = capabilityNameProp ?? routeParams.name;

   const useProvidedData = entityDataProp != null && appKeyProp != null && capabilityNameProp != null;
   const fetched = useCapabilityDetailData(appKey!, name!);
   const data = useProvidedData ? entityDataProp : fetched.data;
   const isLoading = useProvidedData ? false : fetched.isLoading;

   const { setHeaderCenter, setHeaderVersionSelector, setHeaderTitle, pushHeaderLayer, removeHeaderLayer } = useHeaderAction();
   const shell = useCapabilityDetailShell();
   const updateMutation = useCapabilityUpdate(appKey ?? '', name ?? '');
   const [internalOverviewPatch, setInternalOverviewPatch] = useState<Record<string, unknown>>({});
   const usesParentOverviewPatch =
      overviewPersistMode === 'deferred' && onOverviewDeferredDirtyChange !== undefined;
   const usesInternalDeferredSave =
      overviewPersistMode === 'deferred' && onOverviewDeferredDirtyChange === undefined;

   const handleInternalDeferredChange = useCallback((payload: DeferredOverviewDirtyPayload) => {
      setInternalOverviewPatch((prev) => {
         const next = payload.patch;
         if (JSON.stringify(prev) === JSON.stringify(next)) return prev;
         return next;
      });
   }, []);

   const internalPatchRef = useRef(internalOverviewPatch);
   internalPatchRef.current = internalOverviewPatch;

   const saveInternalOverview = useCallback(async () => {
      if (!appKey || !name) return;
      const patch = internalPatchRef.current;
      if (Object.keys(patch).length === 0) return;
      try {
         await updateMutation.mutateAsync(patch);
         toast.success('Capability updated');
      } catch {
         toast.error('Update failed', { description: 'Could not save changes. Try again.' });
      }
   }, [appKey, name, updateMutation]);

   const saveInternalRef = useRef(saveInternalOverview);
   saveInternalRef.current = saveInternalOverview;
   const stableSaveInternalOverview = useCallback(() => void saveInternalRef.current(), []);

   const supplementalRef = useRef(supplementalDeferredSave);
   supplementalRef.current = supplementalDeferredSave;
   const saveWithSupplemental = useCallback(async () => {
      try {
         const sup = supplementalRef.current;
         if (sup?.isDirty) {
            await sup.save();
         }
         await saveInternalRef.current();
      } catch {
         // Individual save handlers show their own toasts
      }
   }, []);

   const setOverviewReg = shell?.setOverviewDeferredSave;

   const activeRevisionIdForPublishedCheck = selectedRevisionId ?? WORKING_COPY_ID;
   const isViewingPublishedForSave = activeRevisionIdForPublishedCheck !== WORKING_COPY_ID;

   useEffect(() => {
      shell?.setIsViewingPublished(isViewingPublishedForSave);
   }, [isViewingPublishedForSave, shell]);

   useEffect(() => {
      shell?.setSelectedRevisionId(activeRevisionIdForPublishedCheck);
   }, [activeRevisionIdForPublishedCheck, shell]);

   const supplementalDirty = supplementalDeferredSave?.isDirty ?? false;
   const supplementalSaving = supplementalDeferredSave?.isSaving ?? false;
   const hasSupplementalSave = supplementalDeferredSave != null;

   useEffect(() => {
      if (!usesInternalDeferredSave || !setOverviewReg) return;

      if (isViewingPublishedForSave) {
         setOverviewReg(null);
         return;
      }

      const overviewDirty = Object.keys(internalOverviewPatch).length > 0;
      const dirty = overviewDirty || supplementalDirty;
      const isSaving = updateMutation.isPending || supplementalSaving;
      setOverviewReg({
         isDirty: dirty,
         isSaving,
         save: hasSupplementalSave ? saveWithSupplemental : stableSaveInternalOverview,
      });
   }, [
      usesInternalDeferredSave,
      setOverviewReg,
      isViewingPublishedForSave,
      internalOverviewPatch,
      updateMutation.isPending,
      stableSaveInternalOverview,
      supplementalDirty,
      supplementalSaving,
      hasSupplementalSave,
      saveWithSupplemental,
   ]);

   useEffect(() => {
      if (!usesInternalDeferredSave || !setOverviewReg) return;
      return () => setOverviewReg(null);
   }, [usesInternalDeferredSave, setOverviewReg]);

   const deferredDirtyHandler = usesParentOverviewPatch
      ? onOverviewDeferredDirtyChange
      : usesInternalDeferredSave
        ? handleInternalDeferredChange
        : undefined;

   const overviewTabFormDisabled =
      overviewFormDisabled ||
      isViewingPublishedForSave ||
      (usesInternalDeferredSave && updateMutation.isPending);

   const titleHeading = data
      ? capabilityDisplayTitle({
         name: (data as { name: string }).name,
         display_name: (data as { display_name?: string }).display_name,
      })
      : name ?? '';

   usePageTitle(`${typeLabel} - ${titleHeading}`);

   useEffect(() => {
      if (isLoading || !data) return;
      const colors = getCapabilityColors((data as { type: string }).type);
      const IconComp = colors.icon;
      const statusStyle = getStatusStyle((data as { status: string }).status);
      const isArchived = (((data as { status?: string }).status as string) ?? '').toLowerCase() === 'archived';
      const typeStr = (data as { type: string }).type;
      const badges = (
         <div className="flex flex-wrap items-center gap-1.5 shrink-0">
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colors.bgLight} ${colors.text}`}>
               {CAPABILITY_TYPE_LABELS[typeStr] || typeStr}
            </span>
            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${statusStyle}`}>
               {(data as { status: string }).status}
            </span>
            <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-600">
               {(data as { visibility?: string }).visibility || 'account'}
            </span>
            {isArchived && (
               <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700">
                  <Lock size={11} /> Archived
               </span>
            )}
            {(data as { domain?: string }).domain && (
               <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                  {(data as { domain: string }).domain}
               </span>
            )}
            {(data as { is_system?: boolean }).is_system && (
               <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                  System
               </span>
            )}
            {typeof (data as { revision?: number }).revision === 'number' && (
               <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                  Rev {(data as { revision: number }).revision}
               </span>
            )}
         </div>
      );
      const titlePayload = {
         icon: (
            <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${colors.bgLight}`}>
               <IconComp size={16} className={colors.text} />
            </div>
         ),
         title: titleHeading,
         subtitle: (data as { fqcn?: string }).fqcn || `${(data as { app_key: string }).app_key}/${(data as { name: string }).name}`,
         badges,
      };
      if (headerLayerId) {
         pushHeaderLayer(headerLayerId, { title: titlePayload });
      } else {
         setHeaderTitle(titlePayload);
      }
      return () => {
         if (headerLayerId) {
            // Cleanup handled by removeHeaderLayer at the page/window level
         } else {
            setHeaderTitle(null);
         }
      };
   }, [isLoading, data, titleHeading, headerLayerId, setHeaderTitle, pushHeaderLayer]);

   useEffect(() => {
      if (!onRevisionChange || !data) return;
      const hasWR = (data as { working_revision?: unknown }).working_revision != null;
      const immRevs: CapabilityRevision[] = (data as { immutable_revisions?: CapabilityRevision[] }).immutable_revisions ?? [];
      const currentId = selectedRevisionId ?? WORKING_COPY_ID;
      if (!hasWR && currentId === WORKING_COPY_ID && immRevs.length > 0) {
         onRevisionChange(immRevs[0].id);
      }
   }, [data, selectedRevisionId, onRevisionChange]);

   const prevWCVRef = useRef(shell?.workingCopyVersion ?? 0);
   useEffect(() => {
      const wcv = shell?.workingCopyVersion ?? 0;
      if (wcv > prevWCVRef.current && onRevisionChange) {
         onRevisionChange(WORKING_COPY_ID);
      }
      prevWCVRef.current = wcv;
   }, [shell?.workingCopyVersion, onRevisionChange]);

   const hasWorkingRev = (data as { working_revision?: unknown } | null)?.working_revision != null;
   const immutableRevisions = useMemo<CapabilityRevision[]>(
      () => (data as { immutable_revisions?: CapabilityRevision[] } | null)?.immutable_revisions ?? [],
      [(data as { immutable_revisions?: CapabilityRevision[] } | null)?.immutable_revisions],
   );
   const hasVersionSelector = onRevisionChange != null && (hasWorkingRev || immutableRevisions.length > 0);
   const activeRevisionId = selectedRevisionId ?? WORKING_COPY_ID;
   const isViewingPublished = activeRevisionId !== WORKING_COPY_ID;

   const isSettingsOpen = shell?.isSettingsOpen ?? false;
   const setIsSettingsOpen = shell?.setIsSettingsOpen;
   const lastHeaderCenterKeyRef = useRef<string | null>(null);
   const lastHeaderVersionKeyRef = useRef<string | null>(null);

   useEffect(() => {
      const nextCenterKey =
         isLoading || !data || !appKey
            ? null
            : `${headerLayerId ?? '__base__'}:${appKey}:${(data as { name?: string }).name ?? ''}:${(data as { type?: string }).type ?? ''}`;
      if (lastHeaderCenterKeyRef.current === nextCenterKey) return;
      lastHeaderCenterKeyRef.current = nextCenterKey;

      if (nextCenterKey == null) {
         if (headerLayerId) {
            pushHeaderLayer(headerLayerId, { center: null });
         } else {
            setHeaderCenter(null);
         }
         return;
      }
      const centerNode = (
         <WorkerHostingInfo
            capabilityName={(data as { name: string }).name}
            capabilityType={(data as { type: string }).type}
            appKey={appKey}
            variant="header"
         />
      );
      if (headerLayerId) {
         pushHeaderLayer(headerLayerId, { center: centerNode });
      } else {
         setHeaderCenter(centerNode);
      }
      return () => {
         if (headerLayerId) {
            // Cleanup handled by removeHeaderLayer at the page/window level
         } else {
            setHeaderCenter(null);
         }
      };
   }, [isLoading, data, appKey, headerLayerId, setHeaderCenter, pushHeaderLayer]);

   useEffect(() => {
      const revisionKey = immutableRevisions
         .map((rev) => `${rev.id}:${rev.version_string}:${rev.status}`)
         .join('|');
      const nextVersionKey =
         isLoading || !data || !hasVersionSelector || !onRevisionChange
            ? null
            : [
                 headerLayerId ?? '__base__',
                 activeRevisionId,
                 String(hasWorkingRev),
                 String(isViewingPublished),
                 revisionKey,
             ].join(':');
      if (lastHeaderVersionKeyRef.current === nextVersionKey) return;
      lastHeaderVersionKeyRef.current = nextVersionKey;

      if (nextVersionKey == null) {
         if (headerLayerId) {
            pushHeaderLayer(headerLayerId, { versionSelector: null });
         } else {
            setHeaderVersionSelector(null);
         }
         return;
      }
      const versionSelectorNode = (
         <div className="flex items-center gap-2">
            <Select value={activeRevisionId} onValueChange={onRevisionChange}>
               <SelectTrigger className="h-7 w-auto max-w-[280px] text-xs gap-1">
                  <GitBranch size={11} className="shrink-0" />
                  <SelectValue />
               </SelectTrigger>
               <SelectContent>
                  {hasWorkingRev && (
                     <SelectItem value={WORKING_COPY_ID}>
                        Working Copy
                        {(() => {
                           const baseId = (data as { working_revision?: { base_revision_id?: string } }).working_revision?.base_revision_id;
                           if (!baseId) return null;
                           const baseRev = immutableRevisions.find((r: CapabilityRevision) => r.id === baseId);
                           return baseRev ? ` (from v${baseRev.version_string})` : null;
                        })()}
                     </SelectItem>
                  )}
                  {immutableRevisions.map((rev: CapabilityRevision) => (
                     <SelectItem key={rev.id} value={rev.id}>
                        v{rev.version_string}
                        {rev.status === 'deprecated' ? ' (deprecated)' : ''}
                     </SelectItem>
                  ))}
               </SelectContent>
            </Select>
            {isViewingPublished && (
               <span className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-medium text-indigo-600">
                  Read-only
               </span>
            )}
         </div>
      );
      if (headerLayerId) {
         pushHeaderLayer(headerLayerId, { versionSelector: versionSelectorNode });
      } else {
         setHeaderVersionSelector(versionSelectorNode);
      }
      return () => {
         if (headerLayerId) {
            // Cleanup handled by removeHeaderLayer at the page/window level
         } else {
            setHeaderVersionSelector(null);
         }
      };
   }, [isLoading, data, hasVersionSelector, hasWorkingRev, activeRevisionId, isViewingPublished, immutableRevisions, onRevisionChange, headerLayerId, setHeaderVersionSelector, pushHeaderLayer]);

   if (isLoading) return <div className="p-6">Loading...</div>;
   if (!data) return <div className="p-6">Not found</div>;

   return (
      <div className={fullBleed ? 'flex h-full min-h-0 flex-1 flex-col' : 'space-y-2 p-12 pt-2'}>
         {/* Primary content area */}
         <div className={fullBleed ? 'flex min-h-0 flex-1 flex-col' : 'px-10'}>
            {children}
         </div>

         {/* Settings bottom sheet */}
         <CapabilitySettingsSheet
            isOpen={isSettingsOpen}
            onClose={() => setIsSettingsOpen?.(false)}
            data={data as Record<string, unknown>}
            typeLabel={typeLabel}
            overviewPersistMode={overviewPersistMode}
            onOverviewDeferredDirtyChange={deferredDirtyHandler}
            pendingOverviewPatch={
               pendingOverviewPatch ??
               (usesInternalDeferredSave ? internalOverviewPatch : undefined)
            }
            overviewFormDisabled={overviewTabFormDisabled}
            overviewChildren={overviewChildren}
            hasWorkingRevision={hasWorkingRev}
            onSaveDeferred={
               onSaveDeferred ??
               (usesInternalDeferredSave
                  ? () => void (hasSupplementalSave ? saveWithSupplemental() : saveInternalOverview())
                  : undefined)
            }
            isOverviewDirty={
               isOverviewDirty ||
               (usesInternalDeferredSave && Object.keys(internalOverviewPatch).length > 0) ||
               supplementalDirty
            }
            isSaving={isSavingOverview || (usesInternalDeferredSave && (updateMutation.isPending || supplementalSaving))}
         />
      </div>
   );
}
